import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, MessageSquare, CalendarDays } from "lucide-react";
import { IMAGES, CONTACT } from "../config/site";

const HeroLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y, scale }}>
        <img src={IMAGES.hero} alt="Pacific sunset off the Honolulu shoreline" className="w-full h-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-charcoal/20" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="text-xs tracking-[0.3em] uppercase font-semibold text-ivory/80 mb-6"
          data-testid="hero-overline"
        >
          REALTOR&reg;, eXp Realty &middot; Oahu Buyer&rsquo;s Agent
        </motion.p>

        <h1 className="font-serif font-light text-ivory tracking-tight leading-[0.95]" data-testid="hero-headline">
          <HeroLine delay={0.35} className="text-6xl sm:text-7xl lg:text-8xl">Marlene Alesna</HeroLine>
          <HeroLine delay={0.5} className="italic text-sand text-3xl sm:text-4xl lg:text-5xl mt-4">Your buyer&rsquo;s agent for Kali&rsquo;u Ala Moana</HeroLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-xl text-base md:text-lg text-ivory/85 leading-relaxed"
          data-testid="hero-subhead"
        >
          Independent representation for buyers looking at the new 728 Ala Moana Boulevard condominium &mdash; one-, two-, and three-bedroom residences from $890,000.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => lenis?.scrollTo("#priority-list", { offset: -60, duration: 1.6 })}
            className="group relative overflow-hidden bg-teal text-ivory px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold"
            data-testid="hero-join-cta"
          >
            <span className="absolute inset-0 bg-ivory translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
            <span className="relative group-hover:text-teal transition-colors duration-500">Join the Buyer List</span>
          </button>
          <a
            href={CONTACT.smsHref}
            className="group flex items-center justify-center gap-3 border border-ivory/60 text-ivory px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-ivory/10 transition-colors duration-500"
            data-testid="hero-text-cta"
          >
            <MessageSquare size={15} strokeWidth={1.5} /> Text Marlene
          </a>
          <a
            href={CONTACT.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-center gap-3 border border-ivory/60 text-ivory px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-ivory/10 transition-colors duration-500"
            data-testid="hero-tour-cta"
          >
            <CalendarDays size={15} strokeWidth={1.5} /> Book a Tour
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 right-6 md:right-12 z-10 text-ivory/70"
      >
        <ArrowDown size={18} strokeWidth={1.5} className="animate-bounce" />
      </motion.div>
    </section>
  );
};

export default Hero;
